import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Search, RefreshCw, Download, Printer, Filter, Calendar, FileText, User, Hash, CheckCircle, ChevronDown, RotateCcw, Package } from 'lucide-react';
import MainService from '../services/mainservice';
import { useToast } from '../contexts/ToastContext';
import * as XLSX from 'xlsx';
import { useLanguage } from '../contexts/LanguageContext';

const ReturnReport = () => {
  const { t } = useLanguage();
  const { showError, showSuccess } = useToast();
  const [loading, setLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [returns, setReturns] = useState([]);
  const [allReturns, setAllReturns] = useState([]); // For Excel export
  const [hasMore, setHasMore] = useState(true);
  const [skip, setSkip] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const observerRef = useRef();
  const [filter, setFilter] = useState({
    dateFrom: new Date().toISOString().split('T')[0],
    dateTo: new Date().toISOString().split('T')[0],
    keyword: '',
  });

  useEffect(() => {
    loadReturns();
  }, []);

  const loadReturns = useCallback(async (reset = true) => {
    if (reset) {
      setLoading(true);
      setSkip(0);
      setReturns([]);
      setAllReturns([]);
      setHasMore(true);
    } else {
      setIsLoadingMore(true);
    }

    const tokenData = localStorage.getItem('AINSOFT_DRIVER_TOKEN');
    if (!tokenData) {
      showError('Please login first');
      return;
    }

    const userData = JSON.parse(tokenData);

    try {
      const filterData = {
        DateFrom: new Date(filter.dateFrom),
        DateTo: new Date(filter.dateTo),
        Keyword: filter.keyword,
        ClientIds: [],
        FromMonth: new Date(filter.dateFrom).getMonth() + 1,
        FromDay: new Date(filter.dateFrom).getDate(),
        FromYear: new Date(filter.dateFrom).getFullYear(),
        ToMonth: new Date(filter.dateTo).getMonth() + 1,
        ToDay: new Date(filter.dateTo).getDate(),
        ToYear: new Date(filter.dateTo).getFullYear(),
        ClientId: userData.ClientId,
        Skip: reset ? 0 : skip,
        Take: 20
      };

      const response = await MainService.ClientReturnClientInList(filterData, userData.Token);
      
      let returnsData = [];
      let totalCount = 0;

      // Handle response structure similar to CustomerReport
      if (response && response.Data && Array.isArray(response.Data)) {
        returnsData = response.Data;
        totalCount = response.Total || response.Data.length;
      } else if (Array.isArray(response)) {
        returnsData = response;
        totalCount = response.length;
      } else if (response && Array.isArray(response.data)) {
        returnsData = response.data;
        totalCount = response.data.length;
      } else {
        console.warn('Unexpected returns response structure:', response);
        returnsData = [];
        totalCount = 0;
      }

      if (reset) {
        setReturns(returnsData);
        setAllReturns(returnsData);
        setTotalCount(totalCount);
        showSuccess(`Loaded ${returnsData.length} return records successfully`);
        console.log('Reset load - Returns:', returnsData.length, 'Total:', totalCount);
      } else {
        setReturns(prev => {
          const newReturns = [...prev, ...returnsData];
          console.log('Appending returns - Previous:', prev.length, 'New:', returnsData.length, 'Total:', newReturns.length);
          return newReturns;
        });
        setAllReturns(prev => {
          const newAllReturns = [...prev, ...returnsData];
          console.log('Appending to allReturns - Previous:', prev.length, 'New:', returnsData.length, 'Total:', newAllReturns.length);
          return newAllReturns;
        });
      }

      setSkip(prev => prev + 20);
      // Check if we have more returns to load
      const currentLoadedCount = reset ? returnsData.length : (allReturns.length + returnsData.length);
      const hasMoreData = currentLoadedCount < totalCount;
      setHasMore(hasMoreData);

      console.log('Pagination Debug:', {
        returnsDataLength: returnsData.length,
        currentLoadedCount,
        totalCount,
        hasMoreData,
        skip: skip + 20,
        remainingReturns: totalCount - currentLoadedCount
      });
    } catch (error) {
      console.error('Error loading returns:', error);
      showError('Failed to load return reports');
      if (reset) {
        setReturns([]);
        setAllReturns([]);
      }
    } finally {
      setLoading(false);
      setIsLoadingMore(false);
    }
  }, [filter, skip, showError, showSuccess, allReturns.length]);

  const handleFilterChange = (field, value) => {
    setFilter(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFilter = () => {
    loadReturns(true);
  };

  // Infinite scroll observer
  const lastReturnElementRef = useCallback(node => {
    if (isLoadingMore) return;
    if (observerRef.current) observerRef.current.disconnect();
    observerRef.current = new IntersectionObserver(entries => {
      console.log('Observer triggered:', {
        isIntersecting: entries[0].isIntersecting,
        hasMore,
        isLoadingMore,
        currentReturnsLength: returns.length
      });
      if (entries[0].isIntersecting && hasMore) {
        console.log('Loading more returns...');
        loadReturns(false);
      }
    });
    if (node) observerRef.current.observe(node);
  }, [isLoadingMore, hasMore, loadReturns, returns.length]);

  const handleRefresh = () => {
    setFilter({
      dateFrom: new Date().toISOString().split('T')[0],
      dateTo: new Date().toISOString().split('T')[0],
      keyword: '',
    });
    loadReturns(true);
  };

  const handlePrint = async (returnData) => {
    try {
      setIsPrinting(true);

      const tokenData = localStorage.getItem('AINSOFT_DRIVER_TOKEN');
      if (!tokenData) {
        showError('Please login first');
        return;
      }

      const userData = JSON.parse(tokenData);

      // Print individual return
      const response = await MainService.PrintIndClientReturn(returnData.Id, userData.Token);
      if (response) {
        window.open(`${process.env.REACT_APP_BASE_PATH || ''}/ReportFiles/${response}`, '_blank');
        showSuccess('Print request sent successfully');
      } else {
        showError('Print Error');
      }
    } catch (error) {
      console.error('Print error:', error);
      showError('Print Error');
    } finally {
      setIsPrinting(false);
    }
  };

  const handleExportToExcel = () => {
    if (!Array.isArray(allReturns) || allReturns.length === 0) {
      showError('No data to export');
      return;
    }

    try {
      // Prepare data for Excel export
      const excelData = allReturns.map((returnItem, index) => ({
        'S.No': index + 1,
        'Date': formatDate(returnItem.ClientReturn?.Date || returnItem.Date),
        'Ref No': returnItem.RefNo || 'N/A',
        'Main Ref No': returnItem.ClientReturn?.RefNo || 'N/A',
        'Total Orders': returnItem.TotalOrders || 'N/A',
        'Status': returnItem.Status?.Name || returnItem.Status || 'N/A'
      }));

      // Create workbook and worksheet
      const ws = XLSX.utils.json_to_sheet(excelData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Return Reports');

      // Generate filename with current date
      const currentDate = new Date().toISOString().split('T')[0];
      const filename = `Return_Reports_${currentDate}.xlsx`;

      // Save the file
      XLSX.writeFile(wb, filename);
      showSuccess('Excel file exported successfully!');
    } catch (error) {
      console.error('Excel export error:', error);
      showError('Failed to export Excel file');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB');
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'N/A';
    return `AED ${parseFloat(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const getStatusColor = (status) => {
    const statusStr = typeof status === 'object' ? status?.Name || status?.name : status;
    switch (statusStr?.toLowerCase()) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'approved':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'rejected':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'processed':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <div className="page-container bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('returnReport.title')}</h1>
          <p className="text-gray-600">{t('returnReport.subtitle')}</p>
        </div>

        {/* Filter Section */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-bold text-gray-900">{t('returnReport.filters')}</h2>
              <p className="text-sm text-gray-600 mt-1">Filter your return records by date and keywords</p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={handleFilter}
                className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200 flex items-center space-x-2 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                <Filter className="w-5 h-5" />
                <span>{t('common.applyFilters')}</span>
              </button>
              <button
                onClick={handleRefresh}
                className="bg-gradient-to-r from-gray-100 to-gray-200 hover:from-gray-200 hover:to-gray-300 text-gray-700 font-semibold py-3 px-6 rounded-xl transition-all duration-200 flex items-center space-x-2 shadow-md hover:shadow-lg"
              >
                <RefreshCw className="w-5 h-5" />
                <span>{t('common.reset')}</span>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-800 mb-2 flex items-center">
                <Calendar className="w-4 h-4 mr-2 text-primary-600" />
                From Date
              </label>
              <input
                type="date"
                value={filter.dateFrom}
                onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200 bg-gray-50 hover:bg-white"
              />
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-800 mb-2 flex items-center">
                <Calendar className="w-4 h-4 mr-2 text-primary-600" />
                To Date
              </label>
              <input
                type="date"
                value={filter.dateTo}
                onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200 bg-gray-50 hover:bg-white"
              />
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-800 mb-2 flex items-center">
                <Search className="w-4 h-4 mr-2 text-primary-600" />
                Keyword
              </label>
              <input
                type="text"
                placeholder="Search returns..."
                value={filter.keyword}
                onChange={(e) => handleFilterChange('keyword', e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200 bg-gray-50 hover:bg-white placeholder-gray-400"
              />
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Records</p>
                <p className="text-2xl font-bold text-gray-900">{totalCount}</p>
              </div>
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                <RotateCcw className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Loaded Records</p>
                <p className="text-2xl font-bold text-gray-900">{allReturns.length}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Returns Table */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-bold text-gray-900">{t('returnReport.records')}</h2>
              <p className="text-sm text-gray-600 mt-1">Detailed view of all return records</p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={handleExportToExcel}
                className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-semibold py-2 px-4 rounded-lg transition-all duration-200 flex items-center space-x-2 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                <Download className="w-4 h-4" />
                <span>{t('common.exportExcel')}</span>
              </button>
            </div>
          </div>

          <div className="overflow-x-auto overflow-y-visible rounded-xl border border-gray-200">
            <table className="w-full" style={{ minWidth: '800px' }}>
              <thead>
                <tr className="bg-gradient-to-r from-gray-100 to-gray-200">
                  <th className="px-4 py-4 text-center text-xs font-bold text-gray-800 uppercase tracking-wider border-r border-gray-300">#</th>
                  <th className="px-4 py-4 text-left text-xs font-bold text-gray-800 uppercase tracking-wider border-r border-gray-300">Date</th>
                  <th className="px-4 py-4 text-left text-xs font-bold text-gray-800 uppercase tracking-wider border-r border-gray-300">Ref No</th>
                  <th className="px-4 py-4 text-left text-xs font-bold text-gray-800 uppercase tracking-wider border-r border-gray-300">Main Ref No</th>
                  <th className="px-4 py-4 text-center text-xs font-bold text-gray-800 uppercase tracking-wider border-r border-gray-300">Total Orders</th>
                  <th className="px-4 py-4 text-left text-xs font-bold text-gray-800 uppercase tracking-wider border-r border-gray-300">Status</th>
                  <th className="px-4 py-4 text-center text-xs font-bold text-gray-800 uppercase tracking-wider">Options</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center justify-center">
                        <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mb-4">
                          <RefreshCw className="w-6 h-6 animate-spin text-primary-600" />
                        </div>
                        <p className="text-lg font-medium text-gray-700">Loading returns...</p>
                        <p className="text-sm text-gray-500 mt-1">Please wait while we fetch your data</p>
                      </div>
                    </td>
                  </tr>
                ) : !Array.isArray(returns) || returns.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center justify-center">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                          <Package className="w-8 h-8 text-gray-400" />
                        </div>
                        <p className="text-lg font-medium text-gray-700">No returns found</p>
                        <p className="text-sm text-gray-500 mt-1">Try adjusting your filters to see more results</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  returns.map((returnItem, index) => (
                    <tr
                      key={index}
                      className="hover:bg-gray-50 transition-all duration-200 group border-b border-gray-100"
                      ref={index === returns.length - 1 ? lastReturnElementRef : null}
                    >
                      <td className="px-4 py-4 text-center text-sm text-gray-500 font-medium border-r border-gray-100">
                        {index + 1}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-700 border-r border-gray-100">
                        <div className="flex items-center">
                          <span>{formatDate(returnItem.ClientReturn?.Date || returnItem.Date)}</span>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-sm font-bold text-gray-900 group-hover:text-primary-700 border-r border-gray-100">
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                          <span>{returnItem.RefNo || 'N/A'}</span>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-700 border-r border-gray-100">
                        {returnItem.ClientReturn?.RefNo || 'N/A'}
                      </td>
                      <td className="px-4 py-4 text-sm text-center border-r border-gray-100">
                        <span className="inline-flex items-center justify-center w-8 h-8 bg-orange-100 text-orange-800 text-xs font-bold rounded-full">
                          {returnItem.TotalOrders || 'N/A'}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-sm border-r border-gray-100">
                        <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full border ${getStatusColor(returnItem.Status)}`}>
                          {typeof returnItem.Status === 'object' ? returnItem.Status?.Name || 'N/A' : returnItem.Status || 'N/A'}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-center">
                        <button
                          onClick={() => handlePrint(returnItem)}
                          disabled={isPrinting}
                          className="bg-blue-500 hover:bg-blue-600 text-white p-2 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-50"
                          title="Print Return"
                        >
                          {isPrinting ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          ) : (
                            <Printer className="w-4 h-4" />
                          )}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Loading More Indicator */}
          {isLoadingMore && (
            <div className="flex items-center justify-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mr-2"></div>
              <span className="text-gray-600">Loading more returns...</span>
            </div>
          )}

          {/* Load More Button (Fallback) */}
          {hasMore && returns.length > 0 && !isLoadingMore && (
            <div className="text-center py-4">
              <button
                onClick={() => loadReturns(false)}
                className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg transition-all duration-200"
              >
                Load More Returns ({totalCount - allReturns.length} remaining)
              </button>
            </div>
          )}

          {/* End of Results */}
          {!hasMore && returns.length > 0 && !isLoadingMore && (
            <div className="text-center py-4 text-gray-500 text-sm">
              No more returns to load
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default ReturnReport;
